import { XIcon } from 'lucide-react';
import { Button, TextInput } from '@bcecloud/portal-ui';

/** One pricing breakpoint: groups up to `upTo` people cost `credits`. */
export interface TierDraft {
  readonly upTo: string;
  readonly credits: string;
}

/** An empty row for the add button. */
export const EMPTY_TIER: TierDraft = { upTo: '', credits: '' };

/**
 * Parses drafts into wire tiers, dropping incomplete rows.
 *
 * @param drafts - The editor's rows.
 * @returns Tiers ready for the API, in entry order.
 */
export function parseTiers(
  drafts: readonly TierDraft[],
): { upTo: number; credits: number }[] {
  return drafts
    .map((draft) => ({ upTo: Number(draft.upTo), credits: Number(draft.credits) }))
    .filter(
      (tier) =>
        Number.isInteger(tier.upTo) &&
        tier.upTo > 0 &&
        Number.isInteger(tier.credits) &&
        tier.credits >= 0,
    );
}

/**
 * Whether the parsed tiers strictly ascend — mirrors the API's rule so the
 * form can refuse before the server does.
 *
 * @param tiers - Parsed tiers.
 * @returns True when each "up to" exceeds the previous.
 */
export function tiersAscend(tiers: readonly { upTo: number }[]): boolean {
  return tiers.every((tier, index) => index === 0 || tier.upTo > (tiers[index - 1]?.upTo ?? 0));
}

export interface TierEditorProps {
  readonly tiers: readonly TierDraft[];
  readonly onChange: (tiers: readonly TierDraft[]) => void;
  readonly disabled?: boolean;
}

/**
 * The breakpoint rows of a pricing structure: "up to N people → X credits",
 * one row per tier, with add and remove. The increment rule for groups
 * beyond the last row lives in the surrounding form — it is one rule, not a
 * row.
 *
 * @param props - Rows and the change handler.
 * @returns The editor.
 */
export function TierEditor({ tiers, onChange, disabled = false }: TierEditorProps): React.ReactElement {
  /**
   * Replaces one row.
   *
   * @param index - Row position.
   * @param patch - Field to change.
   */
  function update(index: number, patch: Partial<TierDraft>): void {
    onChange(tiers.map((tier, i) => (i === index ? { ...tier, ...patch } : tier)));
  }

  return (
    <div>
      <span className="block text-portal-tiny font-bold">Breakpoints</span>
      <p className="mt-0.5 text-portal-micro text-portal-muted">
        Groups up to each size cost that many credits. The increment rule below prices anything
        beyond the last breakpoint.
      </p>

      <div className="mt-2 space-y-2">
        {tiers.map((tier, index) => (
          // Index keys are safe here: rows are positional drafts with no
          // identity, and remove always splices exactly one position.
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className="flex items-center gap-2">
            <span className="text-portal-tiny text-portal-muted">Up to</span>
            <TextInput
              aria-label={`Breakpoint ${index + 1}: up to how many people`}
              type="number"
              min={1}
              value={tier.upTo}
              disabled={disabled}
              onChange={(event) => update(index, { upTo: event.target.value })}
              className="mt-0 w-[90px]"
              placeholder="30"
            />
            <span className="text-portal-tiny text-portal-muted">people →</span>
            <TextInput
              aria-label={`Breakpoint ${index + 1}: credits`}
              type="number"
              min={0}
              step={0.01}
              value={tier.credits}
              disabled={disabled}
              onChange={(event) => update(index, { credits: event.target.value })}
              className="mt-0 w-[80px]"
              placeholder="5"
            />
            <span className="text-portal-tiny text-portal-muted">credits</span>
            {tiers.length > 1 ? (
              <button
                type="button"
                aria-label={`Remove breakpoint ${index + 1}`}
                disabled={disabled}
                onClick={() => onChange(tiers.filter((_, i) => i !== index))}
                className="grid size-[30px] cursor-pointer place-items-center rounded-portal-button text-portal-muted hover:bg-portal-bg hover:text-rose-600"
              >
                <XIcon aria-hidden className="size-4" />
              </button>
            ) : null}
          </div>
        ))}
      </div>

      <Button
        variant="secondary"
        type="button"
        disabled={disabled}
        onClick={() => onChange([...tiers, EMPTY_TIER])}
        className="mt-2"
      >
        Add breakpoint
      </Button>
    </div>
  );
}
